import { getEnvFromUrl } from '@newfront-insurance/shared-public-config';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { EmailGroup, SharedInbox } from '../components/outlook-mailbox-settings';

interface ChatSettings {
  selectedGroupIds: string[];
  groups: EmailGroup[];
  sharedInboxes: SharedInbox[];
  lastUpdated: string;
}

// Shared storage key used by both Navigator (web) and Outlook add-in surfaces.
// Keeping the Outlook add-in's existing key preserves production data for add-in users.
const CHAT_SETTINGS_CHANGED_EVENT = 'outlook-chat-settings:changed';

function getChatSettingsKey(): string {
  return `${getEnvFromUrl()}-outlook-chat-settings`;
}

function saveChatSettings(settings: ChatSettings): void {
  try {
    localStorage.setItem(getChatSettingsKey(), JSON.stringify(settings));
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent(CHAT_SETTINGS_CHANGED_EVENT, { detail: settings }));
      } catch {
        // ignore dispatch errors
      }
    }
  } catch {
    // ignore storage errors
  }
}

function loadChatSettings(): ChatSettings | null {
  try {
    const settings = localStorage.getItem(getChatSettingsKey());
    return settings ? (JSON.parse(settings) as ChatSettings) : null;
  } catch {
    return null;
  }
}

export interface UseChatSettingsResult {
  groups: EmailGroup[];
  selectedGroupIds: Set<string>;
  sharedInboxes: SharedInbox[];
  isLoading: boolean;
  error: string | null;
  toggleGroup: (groupId: string) => void;
  addSharedInbox: (email: string) => boolean;
  removeSharedInbox: (id: string) => void;
  toggleSharedInbox: (id: string) => void;
  refreshGroups: () => Promise<void>;
}

export interface UseChatSettingsConfig {
  graphClient: unknown;
}

export function useChatSettings({ graphClient }: UseChatSettingsConfig): UseChatSettingsResult {
  const [groups, setGroups] = useState<EmailGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [sharedInboxes, setSharedInboxes] = useState<SharedInbox[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasFetchedRef = useRef(false);

  const saveSettings = useCallback(
    (updatedGroups?: EmailGroup[], updatedSelectedIds?: Set<string>, updatedSharedInboxes?: SharedInbox[]) => {
      const settings: ChatSettings = {
        groups: updatedGroups ?? groups,
        selectedGroupIds: Array.from(updatedSelectedIds ?? selectedGroupIds),
        sharedInboxes: updatedSharedInboxes ?? sharedInboxes,
        lastUpdated: new Date().toISOString(),
      };
      saveChatSettings(settings);
    },
    [groups, selectedGroupIds, sharedInboxes],
  );

  // Load from localStorage on mount
  useEffect(() => {
    const savedSettings = loadChatSettings();
    if (savedSettings) {
      console.log('[Chat Settings] Loaded from localStorage:', savedSettings);
      setGroups(savedSettings.groups || []);
      setSelectedGroupIds(new Set(savedSettings.selectedGroupIds || []));
      setSharedInboxes(savedSettings.sharedInboxes || []);
      setIsLoading(false);
      setIsInitialized(true);
    } else {
      console.log('[Chat Settings] No saved settings found, will fetch from API');
      setIsInitialized(true);
    }
  }, []);

  // Sync with other hook instances in the same window and across tabs
  useEffect(() => {
    const applySettings = (settings: ChatSettings | null) => {
      if (!settings) return;
      setGroups(settings.groups || []);
      setSelectedGroupIds(new Set(settings.selectedGroupIds || []));
      setSharedInboxes(settings.sharedInboxes || []);
    };

    const onCustomChange = (event: Event) => {
      // Prefer detail from custom event when available for cross-tab synchronization
      const custom = event as CustomEvent<ChatSettings | undefined>;
      if (custom.detail) {
        applySettings(custom.detail);
      } else {
        applySettings(loadChatSettings());
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key && typeof e.key === 'string' && e.key.includes('outlook-chat-settings')) {
        try {
          const parsed = e.newValue ? (JSON.parse(e.newValue) as ChatSettings) : null;
          applySettings(parsed);
        } catch {
          console.error('[Chat Settings] Failed to parse chat settings from storage:', e.newValue);
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(CHAT_SETTINGS_CHANGED_EVENT, onCustomChange);
      window.addEventListener('storage', onStorage);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(CHAT_SETTINGS_CHANGED_EVENT, onCustomChange);
        window.removeEventListener('storage', onStorage);
      }
    };
  }, []);

  const fetchGroups = useCallback(async () => {
    if (!graphClient) {
      setError('Graph client not available');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const client = graphClient as {
        api: (path: string) => {
          select: (fields: string) => { get: () => Promise<{ value: Record<string, string | boolean>[] }> };
        };
      };
      const response = await client
        .api('/me/memberOf')
        .select('id,displayName,mail,deletedDateTime,isArchived,mailEnabled,hideFromOutlookClients')
        .get();

      const emailGroups: EmailGroup[] = response.value
        .filter(
          (group: Record<string, string | boolean>) =>
            group.mailEnabled &&
            group.mail &&
            !group.hideFromOutlookClients &&
            !group.isArchived &&
            !group.deletedDateTime,
        )
        .map((group) => ({
          id: group.id as string,
          displayName: (group.displayName as string) || 'Unnamed Group',
          mail: group.mail as string,
        }));

      setGroups(emailGroups);
      saveSettings(emailGroups, selectedGroupIds, sharedInboxes);

      console.log('[Chat Settings] Fetched and saved', emailGroups.length, 'groups');
    } catch (err) {
      console.error('[Chat Settings] Error fetching groups:', err);
      setError('Failed to load email groups');
    } finally {
      setIsLoading(false);
    }
  }, [graphClient, selectedGroupIds, sharedInboxes, saveSettings]);

  // Fetch from API only if we don't have cached data
  useEffect(() => {
    if (isInitialized && groups.length === 0 && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchGroups();
    }
  }, [isInitialized, groups.length, fetchGroups]);

  // Toggle group selection
  const toggleGroup = useCallback(
    (groupId: string) => {
      setSelectedGroupIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(groupId)) {
          newSet.delete(groupId);
        } else {
          newSet.add(groupId);
        }

        saveSettings(groups, newSet, sharedInboxes);
        console.log('[Chat Settings] Updated selection:', Array.from(newSet));
        return newSet;
      });
    },
    [groups, sharedInboxes, saveSettings],
  );

  const addSharedInbox = useCallback(
    (email: string): boolean => {
      const trimmedEmail = email.trim().toLowerCase();

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return false;
      }

      if (sharedInboxes.some((inbox) => inbox.email === trimmedEmail)) {
        return false;
      }

      const newInbox: SharedInbox = {
        id: uuidv4(),
        email: trimmedEmail,
        enabled: true,
      };

      const updatedInboxes = [...sharedInboxes, newInbox];
      setSharedInboxes(updatedInboxes);
      saveSettings(groups, selectedGroupIds, updatedInboxes);
      console.log('[Chat Settings] Added shared inbox:', trimmedEmail);
      return true;
    },
    [sharedInboxes, groups, selectedGroupIds, saveSettings],
  );

  const removeSharedInbox = useCallback(
    (id: string) => {
      const updatedInboxes = sharedInboxes.filter((inbox) => inbox.id !== id);
      setSharedInboxes(updatedInboxes);
      saveSettings(groups, selectedGroupIds, updatedInboxes);
      console.log('[Chat Settings] Removed shared inbox:', id);
    },
    [sharedInboxes, groups, selectedGroupIds, saveSettings],
  );

  const toggleSharedInbox = useCallback(
    (id: string) => {
      const updatedInboxes = sharedInboxes.map((inbox) =>
        inbox.id === id ? { ...inbox, enabled: !inbox.enabled } : inbox,
      );
      setSharedInboxes(updatedInboxes);
      saveSettings(groups, selectedGroupIds, updatedInboxes);
      console.log('[Chat Settings] Toggled shared inbox:', id);
    },
    [sharedInboxes, groups, selectedGroupIds, saveSettings],
  );

  const refreshGroups = useCallback(async () => {
    await fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    selectedGroupIds,
    sharedInboxes,
    isLoading,
    error,
    toggleGroup,
    addSharedInbox,
    removeSharedInbox,
    toggleSharedInbox,
    refreshGroups,
  };
}
